import React, { useRef, useState } from 'react';
import { chatbotImportKqshXmlApi } from '../../../features/chatbot/chatbotApi';
import './KQSHImportPanel.scss';

type ImportResult = {
  inserted: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string; cccd?: string }[];
};

const KQSHImportPanel: React.FC = () => {
  const [vehicleType, setVehicleType] = useState<'moto' | 'oto' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onSubmit = async () => {
    if (!file || !vehicleType) return;
    setImporting(true);
    setResult(null);
    setError(null);
    try {
      const res = await chatbotImportKqshXmlApi(file, vehicleType);
      if (res.EC === 0) {
        setResult(res.DT);
      } else {
        setError(res.EM);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Lỗi không xác định');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="kqsh-import__panel">
      <div className="kqsh-import__icon-wrap">
        <i className="material-icons">upload_file</i>
      </div>

      <div className="kqsh-import__content">
        <h3 className="kqsh-import__title">Nhập kết quả sát hạch từ XML</h3>
        <p className="kqsh-import__desc">
          Tải file XML từ hệ thống sát hạch, chọn loại xe và bấm nhập dữ liệu.
        </p>

        <div className="kqsh-import__vehicle-row">
          <label className="kqsh-import__radio">
            <input
              type="radio"
              name="kqsh-vehicleType"
              value="moto"
              checked={vehicleType === 'moto'}
              onChange={() => setVehicleType('moto')}
            />
            <span>Mô tô</span>
          </label>
          <label className="kqsh-import__radio">
            <input
              type="radio"
              name="kqsh-vehicleType"
              value="oto"
              checked={vehicleType === 'oto'}
              onChange={() => setVehicleType('oto')}
            />
            <span>Ô tô</span>
          </label>
        </div>

        <div className="kqsh-import__file-row">
          <input
            ref={fileRef}
            type="file"
            accept=".xml"
            className="kqsh-import__file-input"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={importing}
          />
        </div>

        <div className="kqsh-import__actions">
          <button
            type="button"
            className="kqsh-import__btn"
            disabled={importing || !vehicleType || !file}
            onClick={() => void onSubmit()}
          >
            {importing ? (
              <>
                <span className="kqsh-import__spinner" />
                Đang nhập…
              </>
            ) : (
              <>
                <i className="material-icons">file_upload</i>
                Nhập dữ liệu
              </>
            )}
          </button>
          {(result || error) && (
            <button
              type="button"
              className="kqsh-import__btn kqsh-import__btn--secondary"
              onClick={reset}
            >
              Nhập file khác
            </button>
          )}
        </div>

        {error && (
          <div className="kqsh-import__result kqsh-import__result--error" role="alert">
            <i className="material-icons">error_outline</i>
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="kqsh-import__result kqsh-import__result--success" role="status">
            <i className="material-icons">check_circle</i>
            <div>
              <p>
                Đã nhập <strong>{result.inserted}</strong> bản ghi mới,
                cập nhật <strong>{result.updated}</strong>,
                bỏ qua <strong>{result.skipped}</strong>.
              </p>
              {result.errors.length > 0 && (
                <details className="kqsh-import__errors">
                  <summary>{result.errors.length} lỗi</summary>
                  <ul>
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        Dòng {e.row}{e.cccd ? ` (${e.cccd})` : ''}: {e.message}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KQSHImportPanel;
